import React from 'react';
import TableFilter from '../Filters/TableFilter';
import './TableHeaderWrapper.scss';
import { currencyUtils, dateFormatter, getAuthenticatedFetchOptions, getLoginRequestHeader, localStorageHelper } from 'utils';
import Calendar from '../Calendar/Calendar';
import { useTranslation } from 'react-i18next';
import FilterMarks from 'Components/Filters/FilterMarks';
import DateAggregationWrapper from 'Containers/DateAggregatorWrapper/DateAggregatorWrapper';
import { Button, Checkbox, Dropdown, Tooltip } from 'antd';
import { DownOutlined, SyncOutlined } from '@ant-design/icons/lib';
import { DownloadOutlined } from '@ant-design/icons';
import { SERVER_URL } from '../../utils/config';
import { useFilters } from './useFilter';
import dayjs from 'dayjs';

const REPORT_KEY_TO_TYPE_MAP: Record<string, string> = {
    casino: 'player-group-revenue-with-round-types',
    games: 'game-revenue-with-round-types',
    players: 'player-revenue-with-round-types',
};

interface IProps {
    getReport: () => void;
    parentReportCallback: Function;
    fromDate: Date;
    toDate: Date;
    reportKey: string;
    titleComponent?: JSX.Element;
    isShowSelectTime?: boolean;
    isShowDateAggregation?: boolean;
    notShowFilters?: boolean;
    context: any;
    currenciesBreakdown?: any;
    dateOfLastRefresh?: Date;
    selectedRoundTypes: string[];
    setSelectedRoundTypes: Function;
}

type RoundType = 'regular' | 'free-round' | 'buy' | 'awarded-feature';

const roundTypeLabels: Record<RoundType, string> = {
    regular: 'Regular Rounds',
    'free-round': 'Free Rounds',
    buy: 'Buy Feature',
    'awarded-feature': 'Awarded Feature',
};

export const fetchReport = async (url: string, requestBody: any, reportKey: string) => {
    const config = getAuthenticatedFetchOptions({
        method: 'POST',
        headers: {
            ...getLoginRequestHeader('application/json'),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
    });

    const response = await fetch(url, config);
    const blob = await response.blob();
    if (response.status === 200) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${reportKey}_${dayjs(requestBody.start).format('DD-MM-YYYY')}-${dayjs(requestBody.end).format('DD-MM-YYYY')}.csv`;
        a.click();
    }
};

const TableHeaderWrapper: React.FC<IProps> = ({
    getReport,
    parentReportCallback,
    fromDate,
    toDate,
    reportKey,
    titleComponent,
    isShowSelectTime,
    isShowDateAggregation,
    notShowFilters,
    context,
    dateOfLastRefresh,
    selectedRoundTypes,
    setSelectedRoundTypes,
}) => {
    const { t } = useTranslation();
    const {
        applyingFilters,
        appliedFilters,
        applyFilters,
        cancelFilters,
        resetFilters,
        onFiltersChangeCallback,
    } = useFilters(reportKey, getReport);

    const brandFeatures = localStorageHelper.getChainedValue('user.brandFeatures');
    const supportedRoundTypes = [
        brandFeatures?.supportsRegularRounds !== false && 'regular',
        brandFeatures?.supportsFreeRounds && 'free-round',
        brandFeatures?.supportsBuyFeature && 'buy',
        brandFeatures?.supportsAwardedFeature && 'awarded-feature',
    ].filter(Boolean) as string[];
    const roundTypeOptions = supportedRoundTypes.map((value) => ({
        label: roundTypeLabels[value as RoundType],
        value,
    }));

    const handleDownloadReport = async () => {
        const requestBody = {
            reportType: REPORT_KEY_TO_TYPE_MAP[reportKey],
            roundTypes: selectedRoundTypes,
            includeTestPlayers:
                localStorageHelper.getChainedValue('user.preferences.displayTestData') || false,
            playerCurrencyIds: currencyUtils.getSelectedCurrenciesIdsData(),
            start: fromDate ? dateFormatter.getFormattedStartDate(fromDate) : dateFormatter.getDefaultDate(),
            end: toDate ? dateFormatter.getFormattedEndDate(toDate) : dateFormatter.getDefaultDate(false),
            timeBreakdown:
                localStorageHelper.getChainedValue('user.reports.aggregationPeriod') || 'day',
            currencyBreakdown: applyingFilters.currenciesBreakdown ? 'currency' : 'none',
        };

        await fetchReport(`${SERVER_URL}/reports/revenue/csv-export`, requestBody, reportKey);
    };

    const onRoundTypesChange = (values: string[]) => {
        if (values.length === 0) {
            return;
        }
        localStorageHelper.setChainedValue('user.reports.roundTypes', values);
        setSelectedRoundTypes(values as RoundType[]);
    };

    const dropdownContent = (
        <div
            style={{
                padding: '8px 12px',
                backgroundColor: '#fff',
                border: '1px solid #f0f0f0',
                borderRadius: '4px',
            }}
        >
            <Checkbox.Group
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                value={selectedRoundTypes}
                onChange={onRoundTypesChange}
            >
                {roundTypeOptions.map((opt) => (
                    <Checkbox 
                        key={opt.value} 
                        value={opt.value}
                        disabled={selectedRoundTypes.length === 1 && selectedRoundTypes.includes(opt.value)}
                    >
                        {opt.label}
                    </Checkbox>
                ))}
            </Checkbox.Group>
        </div>
    );

    const isReportDownloadable = !!REPORT_KEY_TO_TYPE_MAP[reportKey];

    return (
        <div className="table-tools">
            <div className="header-line">
                <div>
                    {titleComponent}
                    {dateOfLastRefresh && (
                        <span style={{ fontSize: '12px', color: '#999' }}>
                            Data updated{' '}
                            {dateFormatter.getFormattedDateOfLastRefresh(dateOfLastRefresh)}
                        </span>
                    )}
                </div>
                <div className="tools">
                    <Tooltip title={t('refresh')}>
                        <Button
                            style={{ marginRight: '8px' }}
                            onClick={getReport}
                            icon={<SyncOutlined />}
                        />
                    </Tooltip>
                    <Dropdown
                        overlay={dropdownContent}
                        trigger={['click']}
                        placement="bottomLeft"
                        className="mr-2"
                    >
                        <Button className="flex justify-between">
                            Round Types
                            <DownOutlined />
                        </Button>
                    </Dropdown>
                    {isShowDateAggregation ? (
                        <DateAggregationWrapper
                            parentReportCallback={parentReportCallback}
                            isShowSelectTime={isShowSelectTime}
                            getReport={getReport}
                            dates={{ fromDate, toDate }}
                            context={context}
                        />
                    ) : (
                        <Calendar
                            parentReportCallback={parentReportCallback}
                            isShowSelectTime={isShowSelectTime}
                            defaultValue={{ fromDate, toDate }}
                            context={context}
                        />
                    )}
                    {isReportDownloadable && (
                        <Button
                            style={{ marginRight: '8px' }}
                            onClick={handleDownloadReport}
                            icon={<DownloadOutlined />}
                        >
                            {t('export_to_csv')}
                        </Button>
                    )}
                    {!notShowFilters && (
                        <TableFilter
                            filters={applyingFilters}
                            applyFilters={applyFilters}
                            onFiltersChangeCallback={onFiltersChangeCallback}
                            cancelFilters={cancelFilters}
                            reportKey={reportKey}
                            context={context}
                        />
                    )}
                </div>
            </div>
            <FilterMarks
                filters={appliedFilters}
                onReset={resetFilters}
                onFiltersChangeCallback={onFiltersChangeCallback}
            />
        </div>
    );
};

export default TableHeaderWrapper;
